// Phase JB.3b — useReferral hook.
//
// Auth-only. Returns the caller's own referral code + share helpers +
// their own real stats (sent / signedUp / converted). N06: we NEVER
// surface platform-wide vanity stats — only this user's numbers.

import { useCallback, useEffect, useState } from "react";
import { getBrowserClient } from "../supabase/browser.ts";
import { useAuthSession } from "../auth/useAuthSession.ts";
import { trackEvent } from "./trackEvent.ts";

export interface ReferralStats {
  /** Total referrals this user has initiated. */
  sent: number;
  /** Referrals where the referee signed up (status >= pending). */
  signedUp: number;
  /** Referrals where the referee converted (status='qualified'/'rewarded'). */
  converted: number;
}

export interface UseReferralResult {
  code: string | null;
  shareUrl: string | null;
  stats: ReferralStats;
  loading: boolean;
  error: string | null;
  copy: () => Promise<boolean>;
  share: () => Promise<boolean>;
}

const EMPTY_STATS: ReferralStats = { sent: 0, signedUp: 0, converted: 0 };

function shareUrlFor(code: string): string {
  const origin = typeof window !== "undefined" && window.location?.origin
    ? window.location.origin
    : "https://strikza.app";
  return `${origin}/?ref=${code}`;
}

export function useReferral(): UseReferralResult {
  const { user, session } = useAuthSession();
  const [code, setCode] = useState<string | null>(null);
  const [stats, setStats] = useState<ReferralStats>(EMPTY_STATS);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      setCode(null);
      setStats(EMPTY_STATS);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    const sb = getBrowserClient();

    void (async () => {
      try {
        // 1. Fetch existing code (RLS scoped to this user).
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const codeRes = await (sb.from("referral_codes" as never) as any)
          .select("code")
          .eq("user_id", user.id)
          .maybeSingle();
        let resolvedCode: string | null = (codeRes.data?.code as string | undefined) ?? null;

        // 2. Issue if absent — call the edge fn with the user's JWT.
        if (!resolvedCode) {
          const token = session?.access_token;
          if (token) {
            const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/jb-referral-code-issue`;
            const r = await fetch(url, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              body: "{}",
            });
            if (r.ok) {
              const json = await r.json();
              if (json?.ok && typeof json.code === "string") {
                resolvedCode = json.code;
              }
            }
          }
        }

        // 3. Fetch this user's referrals (own rows only via RLS).
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const refRes = await (sb.from("referrals" as never) as any)
          .select("status")
          .eq("referrer_user_id", user.id);
        const rows = (refRes.data ?? []) as Array<{ status: string }>;
        const computed: ReferralStats = {
          sent: rows.length,
          signedUp: rows.filter((r) => r.status === "pending" || r.status === "qualified" || r.status === "rewarded").length,
          converted: rows.filter((r) => r.status === "qualified" || r.status === "rewarded").length,
        };

        if (!cancelled) {
          setCode(resolvedCode);
          setStats(computed);
          setLoading(false);
        }
      } catch (e) {
        if (!cancelled) {
          setError((e as Error).message);
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user, session]);

  const shareUrl = code ? shareUrlFor(code) : null;

  const copy = useCallback(async (): Promise<boolean> => {
    if (!shareUrl) return false;
    try {
      if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
      }
      trackEvent("referral_link_copied", { source: "useReferral.copy" });
      return true;
    } catch {
      return false;
    }
  }, [shareUrl]);

  const share = useCallback(async (): Promise<boolean> => {
    if (!shareUrl) return false;
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const nav = navigator as any;
      if (typeof nav?.share === "function") {
        await nav.share({ title: "STRIKZA", text: "Join STRIKZA", url: shareUrl });
        trackEvent("referral_link_copied", { source: "useReferral.share" });
        return true;
      }
    } catch {
      /* fall through to copy */
    }
    return copy();
  }, [shareUrl, copy]);

  return { code, shareUrl, stats, loading, error, copy, share };
}
